import { hasFootprint } from '../HouseMap/sideAddresses';
import type { BuildingWithConvertedAddresses } from './types';

const isFiniteNumber = (n: unknown): n is number =>
  typeof n === 'number' && Number.isFinite(n);

/**
 * Overlays the stored `/building-page` document onto the building the user is
 * already looking at, keeping the live geo facts the caller started from.
 *
 * The stored `building-data` document no longer carries `addresses`,
 * `centroid` or a trustworthy footprint: the backend strips them on insert
 * (utils/building_payload.py) and re-resolves `geometry` from Mapbox, which
 * can come back null. It reliably supplies listings, counts and report data,
 * nothing about WHERE the building is. So whatever the caller already knows —
 * the map click point and tile addresses, or a listing's canonical address —
 * must survive the swap. Replacing the building wholesale drops those fields
 * and every consumer that reads them (Street View, the location map, the share
 * URL's lat/lng) silently degrades or errors.
 *
 * Live data also wins over stored data where both exist: the stored doc can be
 * stale or describe a different building (wrong address/polygon), and Mapbox is
 * the source of truth for geography.
 */
export const mergeStoredBuilding = (
  prev: BuildingWithConvertedAddresses,
  stored: BuildingWithConvertedAddresses,
): BuildingWithConvertedAddresses => {
  const merged: BuildingWithConvertedAddresses = { ...stored };

  if (hasFootprint(prev.geometry)) {
    merged.geometry = prev.geometry;
    merged.geometry_source = prev.geometry_source; // keep tag with geometry
  }

  if (
    prev.centroid &&
    isFiniteNumber(prev.centroid.lng) &&
    isFiniteNumber(prev.centroid.lat)
  ) {
    merged.centroid = prev.centroid;
  }

  const prevHasStreetAddress =
    Array.isArray(prev.addresses) &&
    prev.addresses.length > 0 &&
    Boolean(prev.addresses[0]?.street);
  const storedHasAddresses =
    Array.isArray(stored.addresses) && stored.addresses.length > 0;
  if (prevHasStreetAddress || (!storedHasAddresses && prev.addresses?.length)) {
    merged.addresses = prev.addresses;
  }

  return merged;
};
